import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';

import {
	caseApplicationsService,
	GET_CASES_CLIENTS,
	GET_PINNED_CASES,
	GET_CLIENT_APPROVE_STATUS,
} from '@/api';
import {
	Button,
	CustomProgressBar,
	EmptyFiltersScreen,
	FunnelEmptyScreen,
	Spinner,
} from '@/components/common';
import { EmptyBoxScreen, StatusCard } from '@/components/dashboard';
import { getPercent } from '@/utils';
import { formatErrorMessage } from '@/lib';
import {
	CaseApplications,
	CaseStatusFilter,
	ErrorHttpResponse,
} from '@/interfaces';
import { useDebouncedCallback } from 'use-debounce';
import { useNotification } from '@/hooks';
import { HIDE_PIN_STATUSES_CASE } from '@/const/cases';
import { lawyerDirectoryService } from '@/api';
import { useEventSource } from '@/hooks/useEventSource';
import { useSession } from 'next-auth/react';
import clsx from 'clsx';

interface CaseCardProps {
	item: CaseApplications;
	onClickButton: (id: string) => void;
	onClickPin: (id: string) => void;
}

const CaseCard: React.FC<CaseCardProps> = ({
	item,
	onClickButton,
	onClickPin,
}) => {
	const { data: clientApproveStatus } = useQuery({
		queryKey: [GET_CLIENT_APPROVE_STATUS, item.application_id],
		queryFn: () =>
			lawyerDirectoryService.getClientApproveStatus(item.application_id),
		enabled: !!item.application_id,
	});

	return (
		<StatusCard
			step_name={item.step_name}
			showPin={!HIDE_PIN_STATUSES_CASE.includes(item.progress_status)}
			clientApproveEmail={
				item.progress_status === 'Declined'
					? !clientApproveStatus?.data?.isApproved &&
					  clientApproveStatus?.data?.emailSent
					: false
			}
			key={item.id}
			id={item.id}
			status={item.progress_status}
			isPinned={item.is_pinned}
			textSize="semibold-16"
			title={item.title}
			onClickPin={() => onClickPin(item.id)}
		>
			<div className="space-y-6 !mt-12 lg:!mt-6">
				<CustomProgressBar
					percent={getPercent(item.numerical_order, item.total_steps)}
					stepName={item.step_name}
					status={item.progress_status}
				/>
				<Button
					decoration="line-primary"
					size="small"
					className="3xl:!py-4"
					onClick={() => onClickButton(item.id)}
				>
					View Case
				</Button>
			</div>
		</StatusCard>
	);
};

interface Props {
	setTotal: Dispatch<SetStateAction<number | undefined>>;
	setTotalItems: Dispatch<SetStateAction<number | undefined>>;
	setTotalPages: Dispatch<SetStateAction<number | undefined>>;
}

export const CasesGridMobile: React.FC<Props> = ({
	setTotal,
	setTotalItems,
	setTotalPages,
}) => {
	const router = useRouter();
	const { data: session } = useSession();

	const searchParams = useSearchParams();
	const params = new URLSearchParams(searchParams);
	const queryClient = useQueryClient();
	const { toastError } = useNotification();

	const { data: stepNotification } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/action-resolved/subscribe`,
	});

	const firstParsed = stepNotification ? JSON.parse(stepNotification) : null;
	const parsedNotification = firstParsed ? JSON.parse(firstParsed.data) : null;

	const notClientId = parsedNotification?.clientId;
	const notDate = parsedNotification?.date;
	const userId = session?.user.user.id;

	const [listCasesState, setListCasesState] = useState<CaseApplications[]>();
	const pinningIds = useRef(new Set<string>());

	const search = params.get('search') || '';
	const status =
		params
			.get('status')
			?.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ') || '';

	const {
		data: cases,
		isLoading,
		isFetching,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: [`GET_CASES_CLIENTS_MOBILE`, search, status],
		queryFn: ({ pageParam = 1 }) =>
			caseApplicationsService.getAll({
				page: pageParam,
				limit: 8,
				search: search,
				progressStatusFilter: status as CaseStatusFilter,
			}),
		refetchOnWindowFocus: false,
		getNextPageParam: (lastPage, allPages) => {
			const totalPages = Math.ceil(lastPage.data.totalItems / 8);
			const nextPage = allPages.length + 1;
			return nextPage <= totalPages ? nextPage : undefined;
		},
		initialPageParam: 1,
	});

	useEffect(() => {
		if (cases) {
			const casesData = cases?.pages.flatMap((page) => page.data.items) ?? [];
			const total = cases?.pages[0]?.data.total ?? 0;
			const totalItems = cases?.pages[0]?.data.totalItems ?? 0;
			const totalPages = cases?.pages[0]?.data.totalPages ?? 0;
			setTotal(total);
			setTotalItems(totalItems);
			setTotalPages(totalPages);
			setListCasesState(casesData);
		}
	}, [cases, isFetching]);

	useEffect(() => {
		const timeOut = setTimeout(() => {
			if (userId === notClientId) {
				refetch();
			}
		}, 3000);

		return () => clearTimeout(timeOut);
	}, [notClientId, notDate]);

	const onClickButton = (id: string): void => {
		router.push(`${router.pathname}/${id}`);
	};

	const onClickPin = useDebouncedCallback(async (id: string) => {
		if (pinningIds.current.has(id)) return;
		pinningIds.current.add(id);
		try {
			await caseApplicationsService.togglePinCase(id);
			const mutatedCases = getMutatedCases(id);
			setListCasesState(mutatedCases);
			queryClient.invalidateQueries({
				queryKey: [GET_CASES_CLIENTS, search, status],
			});
			queryClient.invalidateQueries({ queryKey: [GET_PINNED_CASES] });
		} catch (error) {
			toastError(formatErrorMessage((error as ErrorHttpResponse)?.message));
		} finally {
			pinningIds.current.delete(id);
		}
	}, 100);

	const listCases = listCasesState || [];

	const getMutatedCases = (id: string): CaseApplications[] => {
		return listCases.map((item) => {
			if (item.id === id) {
				item = {
					...item,
					is_pinned: !item.is_pinned,
				};
			}
			return item;
		});
	};

	const paramsArray = params?.toString()?.split('&');
	const lastFilterStatus =
		search && status ? paramsArray?.[2].startsWith('status') : false;

	return (
		<div className="w-full lg:hidden">
			<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6 mb-6">
				{listCases.map((item) => (
					<CaseCard
						key={item.id}
						item={item}
						onClickButton={onClickButton}
						onClickPin={onClickPin}
					/>
				))}
			</div>
			<div
				className={clsx('flex w-full justify-center pt-2', {
					hidden: !hasNextPage || listCases.length === 0,
				})}
			>
				<Button
					type="button"
					decoration="line-primary"
					size="fit"
					className="!h-12 !w-fit"
					onClick={() => fetchNextPage()}
					disabled={isFetchingNextPage}
				>
					See More
				</Button>
			</div>
			{(isLoading || isFetchingNextPage) && (
				<div className="flex justify-center w-full">
					<Spinner type="loadingPage" />
				</div>
			)}
			{listCases.length === 0 &&
				((!!search && !status) || (!!search && !lastFilterStatus)) && (
					<EmptyFiltersScreen className="h-[65%] !mt-[-75px]">
						<span className="block">
							No results were found for your search.
						</span>
						<br />
						<span className="block">Please try using different keywords.</span>
					</EmptyFiltersScreen>
				)}

			{listCases.length === 0 &&
				((status && !search) || (!!status && lastFilterStatus)) && (
					<FunnelEmptyScreen
						message="There are currently no cases matching this filter."
						className="!mt-[105px]"
					/>
				)}
			{listCases.length === 0 &&
				router.pathname.includes('lawyerapp') &&
				!search &&
				!status && (
					<EmptyBoxScreen message="The user doesn't have any cases yet." />
				)}
		</div>
	);
};
